import { z } from 'zod';
import { CuidSchema } from './crm.schemas.js';
import { PRIORITIES } from './crm.js';
import { REVIEW_VERDICTS, REWORK_CAUSES } from './agencykpi.js';
import {
  AGENCY_FIELD_KINDS,
  MAX_AGENCY_NOTES,
  MAX_AGENCY_TASK_TITLE,
  MAX_BRIEF_CHARS,
  MAX_TEMPLATE_FIELDS,
  MAX_TEMPLATE_SECTIONS,
  MAX_TEMPLATE_TASKS,
  ONBOARDING_STATUSES,
  PROJECT_COLORS,
  AGENCY_PROJECT_KINDS,
  PROJECT_HEALTHS,
} from './agency.js';

/**
 * مخططات إدارة الوكالة.
 */

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const optionalDate = z.coerce.date().nullable().optional();

/**
 * منطقيّ في سلسلة استعلام.
 *
 * `z.coerce.boolean()` يجعل النصّ "false" صحيحًا (`Boolean('false') === true`)،
 * فمرشّح «غير المكتملة» كان سيعيد كل شيء. التحويل الصريح يقرأ النصّ كما كُتب.
 */
const queryBool = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((v) => v === true || v === 'true')
  .optional();

/* ---------- المشاريع ---------- */


/** قواعد أتمتة المشروع — فارغ الحقل يعطّل قاعدته. */
const ProjectRulesSchema = z.object({
  completeOnSectionId: CuidSchema.nullable().optional(),
  moveOnCompleteSectionId: CuidSchema.nullable().optional(),
});
export const CreateAgencyProjectSchema = z.object({
  name: z.string().trim().min(2, 'اسم المشروع مطلوب.').max(120),
  description: optionalText(1000),
  color: z.enum(PROJECT_COLORS).default(PROJECT_COLORS[0]),
  kind: z.enum(AGENCY_PROJECT_KINDS).default('INTERNAL'),
  accountId: CuidSchema.nullable().optional(),
  contractId: CuidSchema.nullable().optional(),
  ownerId: CuidSchema.nullable().optional(),
  portfolioId: CuidSchema.nullable().optional(),
  goalId: CuidSchema.nullable().optional(),
  /**
   * القالب الذي يُنسخ منه — أقسامُه وحقولُه ومهامُّه الجاهزة.
   *
   * وفارغُه لا يعني «بلا قالب» بل «القالب الافتراضي إن وُجد»: من ضبط
   * قالبًا افتراضيًّا أراده لكل مشروعٍ يُنشأ، ومن لا قالبَ له تُنسخ
   * الأقسامُ الأربعة القديمة.
   */
  templateId: CuidSchema.nullable().optional(),
});
export type CreateAgencyProjectInput = z.infer<typeof CreateAgencyProjectSchema>;

export const UpdateAgencyProjectSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  description: optionalText(1000),
  color: z.enum(PROJECT_COLORS).optional(),
  accountId: CuidSchema.nullable().optional(),
  ownerId: CuidSchema.nullable().optional(),
  health: z.enum(PROJECT_HEALTHS).nullable().optional(),
  kind: z.enum(AGENCY_PROJECT_KINDS).optional(),
  contractId: CuidSchema.nullable().optional(),
  portfolioId: CuidSchema.nullable().optional(),
  goalId: CuidSchema.nullable().optional(),
  rules: ProjectRulesSchema.optional(),
  archived: z.boolean().optional(),
});
export type UpdateAgencyProjectInput = z.infer<typeof UpdateAgencyProjectSchema>;

/* ---------- الحقول المخصَّصة ---------- */

/** خيار قائمة: المعرّف يبقى وإن تغيّرت التسمية، فقيم المهام لا تضيع. */
const FieldOptionSchema = z.object({
  id: z.string().trim().min(1).max(40),
  label: z.string().trim().min(1, 'تسمية الخيار مطلوبة.').max(60),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'لون غير صالح.'),
});

export const CreateAgencyFieldSchema = z.object({
  name: z.string().trim().min(1, 'اسم الحقل مطلوب.').max(60),
  kind: z.enum(AGENCY_FIELD_KINDS).default('TEXT'),
  options: z.array(FieldOptionSchema).max(30).default([]),
});
export type CreateAgencyFieldInput = z.infer<typeof CreateAgencyFieldSchema>;

export const UpdateAgencyFieldSchema = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  options: z.array(FieldOptionSchema).max(30).optional(),
  order: z.number().int().min(0).optional(),
});
export type UpdateAgencyFieldInput = z.infer<typeof UpdateAgencyFieldSchema>;

/** قيم حقول المهمة — مفاتيحها معرّفات حقول المشروع، والخادم يُسقط الغريب. */
const FieldValuesSchema = z
  .record(
    z.string().max(40),
    z.union([
      z.string().max(500),
      z.number(),
      z.boolean(),
      z.array(z.string().max(40)).max(30),
      z.null(),
    ]),
  )
  .refine((v) => Object.keys(v).length <= 40, { message: 'حقول أكثر من اللازم.' });

/* ---------- الأقسام ---------- */

export const CreateAgencySectionSchema = z.object({
  name: z.string().trim().min(1, 'اسم القسم مطلوب.').max(60),
});
export type CreateAgencySectionInput = z.infer<typeof CreateAgencySectionSchema>;

export const UpdateAgencySectionSchema = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  order: z.number().int().min(0).optional(),
});
export type UpdateAgencySectionInput = z.infer<typeof UpdateAgencySectionSchema>;

/* ---------- المهام ---------- */

export const CreateAgencyTaskSchema = z.object({
  projectId: CuidSchema,
  sectionId: CuidSchema.optional(),
  /** مهمةٌ فرعية: تتبع أمًّا وتُهمل أقسام اللوحة. */
  parentId: CuidSchema.optional(),
  title: z.string().trim().min(1, 'عنوان المهمة مطلوب.').max(MAX_AGENCY_TASK_TITLE),
  notes: optionalText(MAX_AGENCY_NOTES),
  priority: z.enum(PRIORITIES).default('NORMAL'),
  startAt: optionalDate,
  dueAt: optionalDate,
  assigneeId: CuidSchema.nullable().optional(),
  accountId: CuidSchema.nullable().optional(),
  /** خدمةٌ من الكتالوج ووحداتها — بها يُحتسب استهلاك الباقة عند الإكمال. */
  serviceId: CuidSchema.nullable().optional(),
  units: z.coerce.number().positive('الوحدات موجبة.').max(100_000).optional(),
  isMilestone: z.boolean().optional(),
  fields: FieldValuesSchema.optional(),
});
export type CreateAgencyTaskInput = z.infer<typeof CreateAgencyTaskSchema>;

export const UpdateAgencyTaskSchema = z.object({
  title: z.string().trim().min(1).max(MAX_AGENCY_TASK_TITLE).optional(),
  notes: optionalText(MAX_AGENCY_NOTES),
  priority: z.enum(PRIORITIES).optional(),
  startAt: optionalDate,
  dueAt: optionalDate,
  assigneeId: CuidSchema.nullable().optional(),
  accountId: CuidSchema.nullable().optional(),
  sectionId: CuidSchema.optional(),
  completed: z.boolean().optional(),
  serviceId: CuidSchema.nullable().optional(),
  units: z.coerce.number().positive('الوحدات موجبة.').max(100_000).optional(),
  isMilestone: z.boolean().optional(),
  fields: FieldValuesSchema.optional(),
});
export type UpdateAgencyTaskInput = z.infer<typeof UpdateAgencyTaskSchema>;

/** نقل بطاقة: قسمٌ هدف وموضعٌ فيه — الخادم يعيد ترقيم القسم كله. */
export const MoveAgencyTaskSchema = z.object({
  sectionId: CuidSchema,
  index: z.number().int().min(0).max(10_000),
});
export type MoveAgencyTaskInput = z.infer<typeof MoveAgencyTaskSchema>;

export const AgencyTaskQuerySchema = z.object({
  projectId: CuidSchema.optional(),
  accountId: CuidSchema.optional(),
  assigneeId: CuidSchema.optional(),
  /** مهامّي أنا — يحلّها الخادم من الجلسة. */
  mine: queryBool,
  completed: queryBool,
  q: z.string().trim().max(200).optional(),
});
export type AgencyTaskQuery = z.infer<typeof AgencyTaskQuerySchema>;

/* ---------- الانضمام ---------- */

export const StartOnboardingSchema = z.object({
  accountId: CuidSchema,
  contractId: CuidSchema.optional(),
});
export type StartOnboardingInput = z.infer<typeof StartOnboardingSchema>;

export const UpdateOnboardingSchema = z.object({
  brief: optionalText(MAX_BRIEF_CHARS),
  status: z.enum(ONBOARDING_STATUSES).optional(),
});
export type UpdateOnboardingInput = z.infer<typeof UpdateOnboardingSchema>;

/* ---------- باقة العميل ---------- */

/** تعيين باقة العميل — فارغٌ يوقف الاشتراك الجاري بلا بديل. */
export const SetRetainerSchema = z.object({
  packageId: CuidSchema.nullable(),
});
export type SetRetainerInput = z.infer<typeof SetRetainerSchema>;

/* ---------- المحافظ ---------- */

export const CreatePortfolioSchema = z.object({
  name: z.string().trim().min(2, 'اسم المحفظة مطلوب.').max(80),
  color: z.enum(PROJECT_COLORS).default(PROJECT_COLORS[4]),
  description: optionalText(400),
});
export type CreatePortfolioInput = z.infer<typeof CreatePortfolioSchema>;

export const UpdatePortfolioSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  color: z.enum(PROJECT_COLORS).optional(),
  description: optionalText(400),
});
export type UpdatePortfolioInput = z.infer<typeof UpdatePortfolioSchema>;

/* ---------- مراجعة التسليم ---------- */

/**
 * حكمٌ على تسليمٍ بعينه.
 *
 * والسببُ مطلوبٌ عند الردّ لا اختياريّ: بلا سببٍ لا يُفرَّق عيبُ التنفيذ
 * من تغيُّر طلبِ العميل، فيُحمَّل المنفّذُ ما ليس بيده. والخدمةُ تفرضه
 * ثانيةً — المخطّطُ يشرح والحارسُ يحرس.
 */
export const ReviewTaskSchema = z
  .object({
    verdict: z.enum(REVIEW_VERDICTS),
    cause: z.enum(REWORK_CAUSES).nullable().optional(),
    feedback: optionalText(2000),
  })
  .refine((v) => v.verdict !== 'REWORK' || Boolean(v.cause), {
    message: 'سببُ الردّ مطلوب.',
    path: ['cause'],
  });
export type ReviewTaskInput = z.infer<typeof ReviewTaskSchema>;

export const KpiQuerySchema = z.object({
  windowDays: z.coerce.number().int().min(30).max(365).optional(),
});
export type KpiQuery = z.infer<typeof KpiQuerySchema>;

/* ---------- قوالب المشاريع ---------- */

const TemplateSectionSchema = z.object({
  name: z.string().trim().min(1, 'اسم القسم مطلوب.').max(60),
});

const TemplateFieldSchema = z.object({
  name: z.string().trim().min(1, 'اسم الحقل مطلوب.').max(60),
  kind: z.enum(AGENCY_FIELD_KINDS).default('TEXT'),
  options: z
    .array(
      z.object({
        id: z.string().trim().min(1).max(40),
        label: z.string().trim().min(1).max(60),
        color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'لون غير صالح.'),
      }),
    )
    .max(30)
    .default([]),
});

const TemplateTaskSchema = z.object({
  title: z.string().trim().min(1, 'عنوان المهمة مطلوب.').max(MAX_AGENCY_TASK_TITLE),
  notes: optionalText(MAX_AGENCY_NOTES),
  offsetDays: z.number().int().min(0).max(365).default(0),
  isMilestone: z.boolean().default(false),
  sectionOrder: z.number().int().min(0).max(MAX_TEMPLATE_SECTIONS).default(0),
});

/**
 * حفظُ القالب كاملًا في طلبٍ واحد.
 *
 * الأقسامُ والحقولُ والمهامّ تُرسل معًا وتُستبدل جملةً: القالبُ وثيقةٌ
 * تُحرَّر لا سجلاتٌ متفرّقة، وحفظُ كلٍّ منها بطلبٍ يترك القالبَ نصفَ
 * محرَّرٍ إن انقطع الاتصال في المنتصف.
 */
export const SaveTemplateSchema = z.object({
  name: z.string().trim().min(2, 'اسم القالب مطلوب.').max(80),
  description: optionalText(400),
  color: z.enum(PROJECT_COLORS).default(PROJECT_COLORS[0]),
  icon: z.string().trim().min(1).max(30).default('briefcase'),
  isDefault: z.boolean().default(false),
  portfolioId: CuidSchema.nullable().optional(),
  projectDescription: optionalText(1000),
  sections: z.array(TemplateSectionSchema).max(MAX_TEMPLATE_SECTIONS).default([]),
  fields: z.array(TemplateFieldSchema).max(MAX_TEMPLATE_FIELDS).default([]),
  tasks: z.array(TemplateTaskSchema).max(MAX_TEMPLATE_TASKS).default([]),
});
export type SaveTemplateInput = z.infer<typeof SaveTemplateSchema>;

export const UpdateTemplateFlagsSchema = z.object({
  isDefault: z.boolean().optional(),
  archived: z.boolean().optional(),
});
export type UpdateTemplateFlagsInput = z.infer<typeof UpdateTemplateFlagsSchema>;

/**
 * نشرُ حقول القالب على مشاريعه القائمة — فعلٌ صريحٌ لا أثرٌ جانبيّ.
 *
 * المشروعُ نسخةٌ من قالبه لحظة إنشائه، فتعديلُ القالب لا يمسّه. ومن أراد
 * أن يرى حقلًا جديدًا في كل مشاريعه فليقلها صراحةً — والخادمُ يضيف
 * الناقصَ ولا يحذف ما زاد: حذفُ حقلٍ يمحو ما كُتب فيه من قيم.
 */
export const ApplyTemplateSchema = z.object({
  /** الافتراضي: كلُّ مشاريع القالب غير المؤرشفة. */
  projectIds: z.array(CuidSchema).max(500).optional(),
  /** أضِف الأقسامَ الناقصة أيضًا لا الحقولَ وحدها. */
  withSections: z.boolean().default(false),
});
export type ApplyTemplateInput = z.infer<typeof ApplyTemplateSchema>;

/* ---------- الأهداف ---------- */

export const CreateGoalSchema = z.object({
  title: z.string().trim().min(2, 'عنوان الهدف مطلوب.').max(160),
  description: optionalText(1000),
  ownerId: CuidSchema.nullable().optional(),
  dueAt: optionalDate,
});
export type CreateGoalInput = z.infer<typeof CreateGoalSchema>;

export const UpdateGoalSchema = z.object({
  title: z.string().trim().min(2).max(160).optional(),
  description: optionalText(1000),
  ownerId: CuidSchema.nullable().optional(),
  dueAt: optionalDate,
  archived: z.boolean().optional(),
});
export type UpdateGoalInput = z.infer<typeof UpdateGoalSchema>;
